import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState, useRef, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Render free tier sleeps: the first request after idle can take 30-60s. Give it
// room instead of hanging forever with no error.
axios.defaults.timeout = 90000;
import '@/index.css';
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import ClientDashboard from '@/pages/ClientDashboard';
import AstrologerDashboard from '@/pages/AstrologerDashboard';
import CreateRequest from '@/pages/CreateRequest';
import RequestList from '@/pages/RequestList';
import AnsweredRequests from '@/pages/AnsweredRequests';
import AstrologerOrders from '@/pages/AstrologerOrders';
import RequestDetail from '@/pages/RequestDetail';
import SubmitAnswer from '@/pages/SubmitAnswer';
import CompareAnswers from '@/pages/CompareAnswers';
import Payment from '@/pages/Payment';
import Chat from '@/pages/Chat';
import AdminDashboard from '@/pages/AdminDashboard';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import { Toaster } from '@/components/ui/sonner';
import LocaleToggle from '@/components/LocaleToggle';
import { getLocale, startSimplifiedConversion } from '@/i18n/locale';
import { loadRates } from '@/data/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_DASHBOARD_PATH = {
  admin: '/admin/dashboard',
  astrologer: '/astrologer/dashboard',
  client: '/client/dashboard',
};

const dashboardPathForRole = (role) => ROLE_DASHBOARD_PATH[role] || '/client/dashboard';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ free_mode: true });
  const [rates, setRates] = useState(null);
  const [displayCurrency, setDisplayCurrencyState] = useState(() => {
    try { return localStorage.getItem('fm_currency') || 'HKD'; } catch (e) { return 'HKD'; }
  });

  const [profileComplete, setProfileComplete] = useState(null); // null = unknown, true/false once known
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // Session-expiry guard: any 401 while signed in → one clear toast + sign-out
  // (ProtectedRoute then redirects to /login). Ends the old infinite
  // 「無法載入資料」 red-toast loop after the 7-day cookie lapsed.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401 && userRef.current) {
          userRef.current = null;
          setUser(null);
          toast.error('登入已過期，請重新登入');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const setDisplayCurrency = useCallback((code) => {
    setDisplayCurrencyState(code);
    try { localStorage.setItem('fm_currency', code); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data || { free_mode: true })).catch(() => {});
    loadRates().then(setRates).catch(() => {});
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Mandatory-profile gate: both clients and astrologers must complete their profile
  // before using any feature. A saved profile is always complete (the write endpoints
  // enforce every required field), so existence == complete.
  const refreshProfile = useCallback(async () => {
    if (!user) { setProfileComplete(null); return; }
    if (user.role === 'admin') { setProfileComplete(true); return; }
    const url = user.role === 'astrologer' ? `${API}/astrologer/profile` : `${API}/client/profile`;
    try {
      const r = await axios.get(url, { withCredentials: true });
      setProfileComplete(!!r.data);
    } catch (e) { /* leave unknown on transient error — don't lock the user out */ }
  }, [user]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      try {
        localStorage.removeItem('fm_request_draft');
        if (userRef.current) localStorage.removeItem(`fm_request_draft:${userRef.current.user_id}`);
      } catch (e) { /* storage unavailable */ }
      setUser(null);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout, config, rates, displayCurrency, setDisplayCurrency, profileComplete, refreshProfile }),
    [user, loading, checkAuth, logout, config, rates, displayCurrency, setDisplayCurrency, profileComplete, refreshProfile]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Loading screen that explains itself when the free-tier backend is cold-starting,
// so a 30-60s first load doesn't read as "the site is broken".
function LoadingScreen() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 5000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-lg">載入中...</p>
      {slow && (
        <p className="text-sm text-[#5C5C5C] max-w-sm">
          伺服器啱啱由休眠中喚醒，首次載入可能需時 30–60 秒，請稍候…
        </p>
      )}
    </div>
  );
}

function ProtectedRoute({ children, requireRole, allowIncompleteProfile = false }) {
  const { user, loading, profileComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (requireRole && user.role !== requireRole) {
      navigate(dashboardPathForRole(user.role), { replace: true });
      return;
    }

    // Mandatory-profile gate: bounce an incomplete profile back to its dashboard,
    // where a non-dismissable profile form blocks everything until it's completed.
    // Routes marked allowIncompleteProfile (e.g. the astrologer request pool) stay
    // browsable read-only — the backend still blocks answering without a profile.
    if (user.role !== 'admin' && profileComplete === false && !allowIncompleteProfile) {
      const dash = dashboardPathForRole(user.role);
      if (location.pathname !== dash) navigate(dash, { replace: true });
    }
  }, [user, loading, requireRole, navigate, profileComplete, location.pathname, allowIncompleteProfile]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return null;
  }

  if (requireRole && user.role !== requireRole) {
    return null;
  }

  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      <Route path="/client/dashboard" element={
        <ProtectedRoute requireRole="client">
          <ClientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/client/create-request" element={
        <ProtectedRoute requireRole="client">
          <CreateRequest />
        </ProtectedRoute>
      } />
      <Route path="/client/request/:requestId" element={
        <ProtectedRoute requireRole="client">
          <RequestDetail />
        </ProtectedRoute>
      } />
      <Route path="/client/compare/:requestId" element={
        <ProtectedRoute requireRole="client">
          <CompareAnswers />
        </ProtectedRoute>
      } />
      <Route path="/client/payment/:requestId/:astrologerId" element={
        <ProtectedRoute requireRole="client">
          <Payment />
        </ProtectedRoute>
      } />

      <Route path="/astrologer/dashboard" element={
        <ProtectedRoute requireRole="astrologer">
          <AstrologerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/astrologer/requests" element={
        <ProtectedRoute requireRole="astrologer" allowIncompleteProfile>
          <RequestList />
        </ProtectedRoute>
      } />
      <Route path="/astrologer/answered" element={
        <ProtectedRoute requireRole="astrologer">
          <AnsweredRequests />
        </ProtectedRoute>
      } />
      <Route path="/astrologer/orders" element={
        <ProtectedRoute requireRole="astrologer">
          <AstrologerOrders />
        </ProtectedRoute>
      } />
      <Route path="/astrologer/request/:requestId" element={
        <ProtectedRoute requireRole="astrologer">
          <SubmitAnswer />
        </ProtectedRoute>
      } />

      <Route path="/chat/:orderId" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />

      <Route path="/admin/dashboard" element={
        <ProtectedRoute requireRole="admin">
          <AdminDashboard />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (getLocale() === 'hans') startSimplifiedConversion();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <LocaleToggle />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL, dashboardPathForRole };
